import { useAuth0 } from "ct-auth"
import { Alert, Box, Button, CircularProgress, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { peekIncidentInvite, joinIncidentChannel } from "../../data-utils/data"
import IncidentJoinScreen from "../incidents/IncidentJoinScreen.jsx"
import { ROUTE_PATHS } from "../../utilities/constants"
import logger from "../../Logger"

const pageLogger = logger.child({ module: "incident-invite-handler" })

// How long the "You're in" confirmation stays on screen before we navigate to
// the incident. Long enough to register success, short enough to not stall.
const JOINED_REDIRECT_DELAY_MS = 1200

const INVALID_INVITE_COPY = "This invite link is no longer valid — it may have been used already or expired. Ask the incident owner to re-invite you."

const CenteredStatus = ({ children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "60vh", gap: 2, px: 2 }}>
    {children}
  </Box>
)

// Handles /incidents/invite?token=... (M4.1 channel join flow). Mirrors the
// gameplay invite-handler.jsx auth state machine (peek → login_hint redirect →
// wrong-user logout) but diverges after auth: instead of resolving the token
// immediately, it renders IncidentJoinScreen and only consumes the token when
// the user explicitly clicks Join — the join is a mutation, so per the repo
// rule it must be click-triggered, never fired from useEffect. (The peek on
// mount is a read; that's fine.)
//
// Phases:
//   loading    → peek / Auth0 in flight
//   wrong_user → logged in as someone else; logging out and returning here
//   invalid    → token is dead: peek 404, or join rejected it (consumed/expired)
//   ready      → authed as the invited email; join screen showing (join
//                failures come back here with an inline error for retry)
//   joining    → join mutation in flight
//   joined     → success flash, then navigate to /incidents/<incidentKey>
//   error      → transient peek failure; message + Retry + "Go to incidents"
export function IncidentInviteHandler() {
  const { isAuthenticated, isLoading, loginWithRedirect, logout, getAccessTokenSilently, user } = useAuth0()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // If Auth0 returned an error (e.g. access_denied from an SSO gate), bail to
  // the login page — same escape hatch as the gameplay handler.
  const authError = searchParams.get("error")
  // Token travels in ?token= so the path stays static ("/incidents/invite")
  // and can be allow-listed verbatim in Auth0's returnTo settings.
  const tokenId = searchParams.get("token")

  const [phase, setPhase] = useState("loading")
  const [invite, setInvite] = useState(null)
  const [errorMsg, setErrorMsg] = useState(null)
  // Bumped by the "Try again" button on a transient peek failure — re-arms the
  // effect below (with handledRef reset) so the peek runs again.
  const [peekAttempt, setPeekAttempt] = useState(0)
  // The effect below must run exactly once per settled auth state. Without the
  // guard, a dependency wiggle after a successful join would re-peek the (now
  // CONSUMED) token, get a 404, and flash the "invalid" screen over the
  // success state.
  const handledRef = useRef(false)
  // Success-flash navigate timer — cleared on unmount so Back/away navigation
  // doesn't get yanked to the incident 1.2s later.
  const redirectTimerRef = useRef(null)

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current)
    }
  }, [])

  useEffect(() => {
    if (isLoading) return
    if (handledRef.current) return

    if (authError) {
      pageLogger.warn(`Auth0 returned error '${authError}', redirecting to login`)
      handledRef.current = true
      navigate(ROUTE_PATHS.ROOT)
      return
    }

    if (!tokenId) {
      pageLogger.warn("No token in query string, redirecting to login")
      handledRef.current = true
      navigate(ROUTE_PATHS.ROOT)
      return
    }

    handledRef.current = true

    const handleInvite = async () => {
      // Peek the token (public, read-only) for the invited email + channel
      // context. `notFound` means missing / expired / already consumed — the
      // server intentionally returns a uniform 404 for all three. Anything
      // else (fetch failure, 5xx, rate limit) is transient and must NOT read
      // as an invalid invite — it gets the retryable error screen instead.
      const peekResult = await peekIncidentInvite({ tokenId })
      if (peekResult?.notFound) {
        pageLogger.warn("Incident invite peek returned 404 (token invalid, expired, or consumed)")
        // Dead token — also drop the login-redirect flag so a stale entry
        // can't confuse a later invite in this tab.
        sessionStorage.removeItem(`ct_incident_invite_redirect_${tokenId}`)
        setPhase("invalid")
        return
      }
      if (!peekResult || peekResult.networkError) {
        pageLogger.warn("Incident invite peek hit a transient error")
        setErrorMsg("We couldn't reach the server to check your invite. Check your connection and try again.")
        setPhase("error")
        return
      }
      const invitedEmail = peekResult.email
      const authHint = peekResult.authHint || "email"

      if (isAuthenticated) {
        // Logged in as a different user — log out and come back here.
        if (invitedEmail && user?.email?.toLowerCase() !== invitedEmail.toLowerCase()) {
          pageLogger.info(`Logged in as '${user?.email}' but invite is for '${invitedEmail}', logging out`)
          setPhase("wrong_user")
          logout({
            logoutParams: {
              returnTo: `${window.location.origin}${process.env.PUBLIC_URL ?? "/"}${ROUTE_PATHS.INCIDENT_INVITE}?token=${encodeURIComponent(tokenId)}`,
            },
          })
          return
        }

        // Correct user — show the join screen. The token is only consumed
        // when they click Join.
        sessionStorage.removeItem(`ct_incident_invite_redirect_${tokenId}`)
        setInvite(peekResult)
        setPhase("ready")
        return
      }

      // Not authenticated — redirect to Auth0 login (only once, to prevent
      // loops). useRef won't survive the full-page Auth0 redirect, so the
      // flag lives in sessionStorage — same pattern as the gameplay handler.
      const REDIRECT_FLAG = `ct_incident_invite_redirect_${tokenId}`
      if (sessionStorage.getItem(REDIRECT_FLAG)) {
        pageLogger.warn("Login redirect already attempted, going to login page")
        sessionStorage.removeItem(REDIRECT_FLAG)
        navigate(ROUTE_PATHS.ROOT)
        return
      }
      sessionStorage.setItem(REDIRECT_FLAG, "1")

      pageLogger.info(`Redirecting to Auth0 login (authHint: ${authHint}${invitedEmail ? `, hint: ${invitedEmail}` : ""})`)
      const authParams = {
        prompt: "login",
      }
      if (authHint === "email") {
        authParams.connection = "email"
      }
      if (invitedEmail) {
        authParams.login_hint = invitedEmail
      }
      // Don't pass organization — the invite token carries the org context.
      // Passing org can cause 400 if the connection isn't enabled for that org.

      loginWithRedirect({
        authorizationParams: authParams,
        appState: { returnTo: `${ROUTE_PATHS.INCIDENT_INVITE}?token=${encodeURIComponent(tokenId)}` },
      })
    }

    handleInvite()
  }, [isAuthenticated, isLoading, tokenId, loginWithRedirect, logout, user, navigate, authError, peekAttempt])

  // Transient peek failure → "Try again" re-runs the peek. handledRef must be
  // re-armed by hand because the effect above sets it before awaiting.
  const retryPeek = () => {
    handledRef.current = false
    setErrorMsg(null)
    setPhase("loading")
    setPeekAttempt((n) => n + 1)
  }

  // Click-triggered join — consumes the token, records joined:true on the
  // caller's channel entries, then navigates to the incident after a short
  // success flash. Never called from an effect.
  const handleJoin = async () => {
    if (phase === "joining" || phase === "joined") return
    setPhase("joining")
    setErrorMsg(null)
    try {
      const accessToken = await getAccessTokenSilently()
      const { result, status, message } = await joinIncidentChannel(accessToken, user.sub, { tokenId })

      if (result?.joined) {
        const incidentKey = result.incidentKey || invite?.incidentKey
        pageLogger.info(`Incident invite joined: incident='${incidentKey}'`)
        setPhase("joined")
        redirectTimerRef.current = setTimeout(() => {
          if (incidentKey) {
            navigate(ROUTE_PATHS.INCIDENT_DETAIL(incidentKey))
          } else {
            navigate(ROUTE_PATHS.INCIDENTS)
          }
        }, JOINED_REDIRECT_DELAY_MS)
        return
      }

      // Hasura wraps the handler's 404 as HTTP 400 with the message in
      // `error`, so match on the message rather than the status code.
      if (status === 404 || /invite not found or expired/i.test(message || "")) {
        pageLogger.warn("Incident invite join rejected (token invalid, expired, consumed, or wrong email)")
        // Dead token — clear the login-redirect flag here too.
        sessionStorage.removeItem(`ct_incident_invite_redirect_${tokenId}`)
        setPhase("invalid")
        return
      }

      // Recoverable failure — stay on the join screen with an inline error so
      // the user can retry in place.
      pageLogger.warn(`Incident invite join failed (status: ${status})`)
      setErrorMsg(message || "Something went wrong while joining. Please try again.")
      setPhase("ready")
    } catch (err) {
      pageLogger.error(`Failed to join incident channel: ${err}`)
      setErrorMsg("Something went wrong while joining. Please try again.")
      setPhase("ready")
    }
  }

  if (phase === "invalid") {
    return (
      <CenteredStatus>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Invite link not valid
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 440, textAlign: "center" }}>
          {INVALID_INVITE_COPY}
        </Typography>
        <Button variant="outlined" onClick={() => navigate(ROUTE_PATHS.INCIDENTS)} sx={{ textTransform: "none" }}>
          Go to incidents
        </Button>
      </CenteredStatus>
    )
  }

  if (phase === "error") {
    // Transient peek failure (join failures render inline on the join screen
    // instead) — offer an in-place retry before the bail-out link.
    return (
      <CenteredStatus>
        <Alert severity="error" sx={{ maxWidth: 440 }}>
          {errorMsg || "Something went wrong. Please try again."}
        </Alert>
        <Button variant="contained" onClick={retryPeek} sx={{ textTransform: "none" }}>
          Try again
        </Button>
        <Button variant="outlined" onClick={() => navigate(ROUTE_PATHS.INCIDENTS)} sx={{ textTransform: "none" }}>
          Go to incidents
        </Button>
      </CenteredStatus>
    )
  }

  if (phase === "ready" || phase === "joining" || phase === "joined") {
    return (
      <IncidentJoinScreen
        channelNames={invite?.channelNames || []}
        invitedEmail={invite?.email || user?.email}
        onJoin={handleJoin}
        joining={phase === "joining"}
        joined={phase === "joined"}
        error={errorMsg}
      />
    )
  }

  // loading / wrong_user — spinner while peek, Auth0, or the account switch
  // settles. wrong_user gets its own line so the logout round-trip doesn't
  // read as a hang.
  return (
    <CenteredStatus>
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        {phase === "wrong_user" ? "Switching accounts for this invite..." : "Loading your incident invite..."}
      </Typography>
    </CenteredStatus>
  )
}
